import React from 'react';
import {
  Box,
  Divider,
  FormLabel,
  Slider,
  Typography,
} from '@mui/material';
import DOMPurify from 'dompurify';

/**
 * Componente para renderizar una pregunta de tipo escala bipolar
 * Permite al usuario seleccionar un valor en una escala de negativo a positivo
 * 
 * @param {Object} props
 * @param {number} props.questionId - ID único de la pregunta
 * @param {string} props.typeQuestion - Tipo de la pregunta
 * @param {string} props.questionName - Texto de la pregunta
 * @param {string} [props.description] - Descripción adicional de la pregunta
 * @param {number} props.index - Índice de la pregunta en el array
 * @param {Array<number>} props.unansweredQuestions - Array de índices de preguntas sin responder
 * @param {Object} props.questionRef - Ref para scroll automático
 * @param {number} props.score - Valor máximo de la escala (se usa como +/- score)
 * @param {number} props.value - Valor actual seleccionado
 * @param {Function} props.onChange - Callback cuando cambia el slider
 * @param {Object} props.textBipolarBar - Textos para los extremos de la escala
 * @param {string} props.textBipolarBar.leftText - Texto del extremo izquierdo (negativo)
 * @param {string} props.textBipolarBar.rightText - Texto del extremo derecho (positivo)
 * @param {string} [props.primaryColor='#03aae4'] - Color primario del tema
 * @param {boolean} [props.showDivider=true] - Si se debe mostrar el divisor al final
 */
const BipolarSliderQuestion = ({
  questionId,
  typeQuestion,
  questionName,
  description,
  index,
  unansweredQuestions = [],
  questionRef,
  score,
  value,
  onChange,
  textBipolarBar = { leftText: '', rightText: '' },
  primaryColor = '#03aae4',
  showDivider = true,
  hasOwnBold,
}) => {
  const hasError = unansweredQuestions.includes(index);
  const minValue = -Math.abs(Number(score));
  const maxValue = Math.abs(Number(score));
  const currentValue = value || 0;

  const marks = [
    { value: minValue, label: String(minValue) },
    { value: 0, label: '' },
    { value: maxValue, label: String(maxValue) },
  ];

  return (
    <>
      <FormLabel
        id={`${questionId}-${typeQuestion}`}
        style={{
          fontSize: '1.1',
          fontWeight: hasOwnBold(questionName) ? 'normal' : 'bold',
          marginBottom: '1.1em',
          color: hasError ? 'red' : 'rgba(0, 0, 0, 0.6)',
        }}
        ref={questionRef}
      >
          <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(questionName) }} />
      </FormLabel>

      {description && (
        <Typography
          variant="caption"
          style={{ display: 'block', fontStyle: 'italic' }}
        >
              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }} />
        </Typography>
      )}

      <Slider
        min={minValue}
        max={maxValue}
        step={1}
        value={currentValue}
        onChange={(event, newValue) => onChange(event, newValue, index)}
        valueLabelDisplay="on"
        marks={marks}
        sx={{
          '& .MuiSlider-track': {
            backgroundColor: currentValue === 0 ? 'transparent' : primaryColor,
            height: 8,
            borderRadius: 4,
            border: 'none',
          },
          '& .MuiSlider-rail': {
            backgroundColor: primaryColor,
            height: 8,
            borderRadius: 4,
            border: 'none',
          },
          '& .MuiSlider-thumb': {
            width: 20,
            height: 20,
            border: 'none',
            boxShadow: 'none',
            backgroundColor: primaryColor,
          },
        }}
      />

      {/* Etiquetas para móvil - vertical */}
      <Box
        display={{ xs: 'flex', md: 'none' }}
        flexDirection="column"
        width="100%"
        mt={1}
        px={2}
      >
        <Typography
          variant="caption"
          style={{
            fontSize: '0.8rem',
            textAlign: 'left',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}
        >
          {minValue}
          <Box
            component="span"
            sx={{ fontSize: '1rem', color: primaryColor, mx: 0.5 }}
          >
            ➔
          </Box>
          {textBipolarBar.leftText}
        </Typography>

        <Typography
          variant="caption"
          style={{
            fontSize: '0.8rem',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}
        >
          {maxValue}
          <Box
            component="span"
            sx={{ fontSize: '1rem', color: primaryColor, mx: 0.5 }}
          >
            ➔
          </Box>
          {textBipolarBar.rightText}
        </Typography>
      </Box>

      {/* Etiquetas para escritorio - horizontal */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          justifyContent: 'space-between',
          marginTop: '4px',
        }}
      >
        <Typography
          variant="caption"
          style={{
            fontSize: '0.8rem',
            textAlign: 'left',
            maxWidth: '40%',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}
        >
          {textBipolarBar.leftText}
        </Typography>

        <Typography
          variant="caption"
          style={{
            fontSize: '0.8rem',
            textAlign: 'right',
            maxWidth: '40%',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}
        >
          {textBipolarBar.rightText}
        </Typography>
      </Box>

      {showDivider && (
        <Divider
          variant="middle"
          sx={{
            marginTop: '5px',
          }}
        />
      )}
    </>
  );
};

export default BipolarSliderQuestion;